import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { Spinner, useToast } from './ui';
import type { DraftIngredient, Language, MealDraft } from '../types';

const T = {
  en: {
    title: 'Add a meal',
    close: 'Cancel',
    namePlaceholder: 'Fricassée of chicken, pancakes, tartiflette…',
    nameLabel: 'What is it called?',
    courseLabel: 'When is it eaten?',
    courses: {
      main: 'Lunch or dinner',
      breakfast: 'Breakfast',
      morning_snack: 'Snack box',
      gouter: 'Goûter',
      dessert: 'Pudding',
    } as Record<string, string>,
    lookUp: 'Look it up',
    looking: 'Working out what goes in it…',
    blank:
      'No cooking assistant is configured, so the ingredients are yours to add. Everything else can be filled in below.',
    unknown: (items: string[]) =>
      `Left out, because they are not in the ingredient list: ${items.join(', ')}.`,
    ingredients: 'Ingredients, per person',
    addIngredient: 'Add an ingredient',
    remove: 'Remove',
    noIngredients:
      'No ingredients yet. Without them this meal can be planned but will never reach the shopping list.',
    who: 'Who is it for?',
    classes: { family: 'The whole family', kids: 'The children', adult: 'The adults' } as Record<string, string>,
    prep: 'Prep (min)',
    cook: 'Cook (min)',
    baked: 'We bake this',
    bakery: 'From the boulangerie',
    save: 'Save this meal',
    saving: 'Saving…',
    saved: (name: string) => `${name} is in your meals now, and can be planned like any other.`,
    describe: 'Description',
    searchAdd: 'Search the ingredient list…',
  },
  fr: {
    title: 'Ajouter un repas',
    close: 'Annuler',
    namePlaceholder: 'Fricassée de poulet, crêpes, tartiflette…',
    nameLabel: 'Comment ça s’appelle ?',
    courseLabel: 'Quand est-ce mangé ?',
    courses: {
      main: 'Déjeuner ou dîner',
      breakfast: 'Petit-déjeuner',
      morning_snack: 'Collation',
      gouter: 'Goûter',
      dessert: 'Dessert',
    } as Record<string, string>,
    lookUp: 'Chercher',
    looking: 'Recherche des ingrédients…',
    blank:
      'Aucun assistant configuré : les ingrédients sont à ajouter à la main. Le reste se remplit ci-dessous.',
    unknown: (items: string[]) =>
      `Ignorés, car absents de la liste d’ingrédients : ${items.join(', ')}.`,
    ingredients: 'Ingrédients, par personne',
    addIngredient: 'Ajouter un ingrédient',
    remove: 'Retirer',
    noIngredients:
      'Aucun ingrédient. Sans eux, ce plat peut être planifié mais n’arrivera jamais sur la liste de courses.',
    who: 'Pour qui ?',
    classes: { family: 'Toute la famille', kids: 'Les enfants', adult: 'Les adultes' } as Record<string, string>,
    prep: 'Préparation (min)',
    cook: 'Cuisson (min)',
    baked: 'Fait maison',
    bakery: 'De la boulangerie',
    save: 'Enregistrer',
    saving: 'Enregistrement…',
    saved: (name: string) => `${name} fait maintenant partie de vos repas.`,
    describe: 'Description',
    searchAdd: 'Chercher un ingrédient…',
  },
} as const;

/**
 * Adding a meal the household actually cooks (§45).
 *
 * Typing the name is the whole interaction worth having — working out that a
 * fricassée needs chicken, cream, mushrooms and stock is exactly the tedious
 * part. So the name goes off to be looked up, and what comes back is a draft
 * to correct rather than a form to fill. Nothing is saved until it is read.
 */
export function AddMealSheet({
  language,
  onSaved,
  onClose,
}: {
  language: Language;
  onSaved: () => void;
  onClose: () => void;
}) {
  const t = T[language];
  const toast = useToast();
  const [name, setName] = useState('');
  const [course, setCourse] = useState('main');
  const [draft, setDraft] = useState<MealDraft | null>(null);
  const [mealClass, setMealClass] = useState('family');
  const [gouterKind, setGouterKind] = useState<'bought' | 'baked'>('baked');
  const [breakfastKind, setBreakfastKind] = useState<'home' | 'bakery'>('home');
  const [busy, setBusy] = useState(false);
  const [library, setLibrary] = useState<Array<{ id: string; name: string }>>([]);
  const [query, setQuery] = useState('');
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameRef.current?.focus();
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const lookUp = async () => {
    if (name.trim().length < 2) return;
    setBusy(true);
    try {
      setDraft(await api.draftMeal(name.trim(), language, course));
      if (library.length === 0) setLibrary(await api.ingredients());
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not look that up');
    } finally {
      setBusy(false);
    }
  };

  const patch = (changes: Partial<MealDraft>) =>
    setDraft((current) => (current ? { ...current, ...changes } : current));

  const setIngredients = (ingredients: DraftIngredient[]) => patch({ ingredients });

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      await api.createMeal({
        name: draft.name,
        nameFr: draft.nameFr,
        description: draft.description,
        course: draft.course,
        mealClass,
        prepMinutes: draft.prepMinutes,
        cookMinutes: draft.cookMinutes,
        protein: draft.protein,
        carbBase: draft.carbBase,
        cuisine: draft.cuisine,
        cookingMethod: draft.cookingMethod,
        notes: draft.notes,
        gouterKind: draft.course === 'gouter' ? gouterKind : null,
        breakfastKind: draft.course === 'breakfast' ? breakfastKind : null,
        ingredients: draft.ingredients.map((i) => ({
          ingredientId: i.ingredientId,
          quantity: i.quantity,
          role: i.role,
        })),
      });
      toast(t.saved(draft.name));
      onSaved();
      onClose();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not save that meal');
    } finally {
      setBusy(false);
    }
  };

  const matches = query.trim()
    ? library
        .filter((i) => i.name.toLowerCase().includes(query.trim().toLowerCase()))
        .filter((i) => !draft?.ingredients.some((d) => d.ingredientId === i.id))
        .slice(0, 6)
    : [];

  return (
    <div className="sheet-backdrop" onClick={onClose} role="presentation">
      <div
        className="sheet"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t.title}
      >
        <div className="sheet-head">
          <h2>{t.title}</h2>
          <button className="btn" onClick={onClose}>
            {t.close}
          </button>
        </div>

        <div className="sheet-body">
          <div className="field">
            <label htmlFor="meal-name">{t.nameLabel}</label>
            <input
              id="meal-name"
              ref={nameRef}
              type="text"
              value={name}
              placeholder={t.namePlaceholder}
              disabled={busy}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && void lookUp()}
            />
          </div>

          <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <label className="row" style={{ gap: 8 }}>
              <span className="dim">{t.courseLabel}</span>
              <select
                value={course}
                disabled={busy}
                onChange={(event) => {
                  setCourse(event.target.value);
                  patch({ course: event.target.value });
                }}
                style={{ width: 'auto' }}
              >
                {Object.entries(t.courses).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <button className="btn primary" disabled={busy || name.trim().length < 2} onClick={() => void lookUp()}>
              {busy && !draft ? t.looking : t.lookUp}
            </button>
          </div>

          {busy && !draft && (
            <div style={{ display: 'grid', placeItems: 'center', padding: 30 }}>
              <Spinner />
            </div>
          )}

          {draft && (
            <div className="stack" style={{ gap: 14, marginTop: 18 }}>
              {draft.source === 'blank' && <p className="banner">{t.blank}</p>}
              {draft.unknownIngredients.length > 0 && (
                <p className="dim">{t.unknown(draft.unknownIngredients)}</p>
              )}

              <div className="field">
                <label htmlFor="meal-desc">{t.describe}</label>
                <input
                  id="meal-desc"
                  type="text"
                  value={draft.description}
                  onChange={(event) => patch({ description: event.target.value })}
                />
              </div>

              <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
                <label className="row" style={{ gap: 8 }}>
                  <span className="dim">{t.who}</span>
                  <select
                    value={mealClass}
                    onChange={(event) => setMealClass(event.target.value)}
                    style={{ width: 'auto' }}
                  >
                    {Object.entries(t.classes).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="row" style={{ gap: 6 }}>
                  <span className="dim">{t.prep}</span>
                  <input
                    type="number"
                    min={0}
                    value={draft.prepMinutes}
                    style={{ width: 80 }}
                    onChange={(event) => patch({ prepMinutes: Number(event.target.value) })}
                  />
                </label>
                <label className="row" style={{ gap: 6 }}>
                  <span className="dim">{t.cook}</span>
                  <input
                    type="number"
                    min={0}
                    value={draft.cookMinutes}
                    style={{ width: 80 }}
                    onChange={(event) => patch({ cookMinutes: Number(event.target.value) })}
                  />
                </label>
                {draft.course === 'gouter' && (
                  <label className="row" style={{ gap: 6 }}>
                    <input
                      type="checkbox"
                      style={{ width: 20, height: 20, minHeight: 0 }}
                      checked={gouterKind === 'baked'}
                      onChange={(event) => setGouterKind(event.target.checked ? 'baked' : 'bought')}
                    />
                    <span className="dim">{t.baked}</span>
                  </label>
                )}
                {draft.course === 'breakfast' && (
                  <label className="row" style={{ gap: 6 }}>
                    <input
                      type="checkbox"
                      style={{ width: 20, height: 20, minHeight: 0 }}
                      checked={breakfastKind === 'bakery'}
                      onChange={(event) => setBreakfastKind(event.target.checked ? 'bakery' : 'home')}
                    />
                    <span className="dim">{t.bakery}</span>
                  </label>
                )}
              </div>

              <h3 className="sheet-group">{t.ingredients}</h3>
              {draft.ingredients.length === 0 && <p className="dim">{t.noIngredients}</p>}
              {draft.ingredients.map((line, index) => (
                <div className="row" key={line.ingredientId} style={{ gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ flex: 1, minWidth: 130 }}>{line.name}</span>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={line.quantity}
                    aria-label={line.name}
                    style={{ width: 90 }}
                    onChange={(event) => {
                      const next = [...draft.ingredients];
                      next[index] = { ...line, quantity: Number(event.target.value) };
                      setIngredients(next);
                    }}
                  />
                  <span className="dim" style={{ width: 40 }}>
                    {line.unit === 'unit' ? '×' : line.unit}
                  </span>
                  <button
                    className="btn icon small"
                    aria-label={`${t.remove} ${line.name}`}
                    onClick={() => setIngredients(draft.ingredients.filter((_, i) => i !== index))}
                  >
                    ×
                  </button>
                </div>
              ))}

              <input
                type="search"
                placeholder={t.searchAdd}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              {matches.length > 0 && (
                <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                  {matches.map((option) => (
                    <button
                      key={option.id}
                      className="btn small"
                      onClick={() => {
                        setIngredients([
                          ...draft.ingredients,
                          {
                            ingredientId: option.id,
                            name: option.name,
                            quantity: 1,
                            unit: 'unit',
                            role: null,
                          },
                        ]);
                        setQuery('');
                      }}
                    >
                      + {option.name}
                    </button>
                  ))}
                </div>
              )}

              <div className="row" style={{ marginTop: 8 }}>
                <button className="btn primary" disabled={busy} onClick={() => void save()}>
                  {busy ? t.saving : t.save}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
